import { Illustration, Typography } from '@saviynt/design-system';
import { useIntl } from 'react-intl';

import { TOKENS } from '../../../../shared/constants';

import './NoFilteredPolicyState.css';
/**
 * NoFilteredPolicyState - Empty state component displayed when no data is available for
 * the current filters.
 *
 * Falls back to default "No Data Available" text when custom props are not provided.
 *
 * @param {Object} props
 * @param {string} [props.title] - Custom heading text. Defaults to "No Data Available".
 * @param {string} [props.description] - Custom description text. Defaults to a generic "no records" message.
 *
 * @returns {JSX.Element} The empty/no-results state UI.
 *
 * @example
 * ```tsx
 * <NoFilteredPolicyState />
 *
 * <NoFilteredPolicyState
 *   title="No matching policies"
 *   description="Try adjusting your filters."
 * />
 * ```
 */

interface NoFilteredPolicyStateProps {
  title?: string;
  description?: string;
}

export default function NoFilteredPolicyState({
  title,
  description,
}: NoFilteredPolicyStateProps) {
  const intl = useIntl();

  const defaultTitle = intl.formatMessage({
    defaultMessage: 'No Data Available',
    id: 'LMr0G5',
    description: 'Message: No Data Available',
  });

  const defaultDescription = intl.formatMessage({
    id: 'DI/Q4Z',
    defaultMessage:
      'There are no records to display at the moment. Please check back later or contact support if you need assistance.',
    description: 'Message: There are no records to display at the moment',
  });

  return (
    <div className='NoFilteredPolicyState'>
      <Illustration kind='Empty' className='Example-Illustration' />
      <div className='NoFilteredPolicyState-text'>
        <Typography
          kind='h1'
          desktop='h1-d'
          mobile='h1-d'
          data-testid='PolicyList-NoRecordFound-title'>
          {title || defaultTitle}
        </Typography>
        <Typography
          kind='body1'
          desktop='body1-d'
          mobile='body1-d'
          data-testid='PolicyList-NoRecordFound-description'
          color={TOKENS.CF_NEU_STL}>
          {description || defaultDescription}
        </Typography>
      </div>
    </div>
  );
}
